import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Inline editable field — looks like plain text until clicked, saves on blur.
 *  Use for review-page editing of any question column.
 *
 *  Props:
 *  - questionId: target question id (PATCH /questions/{id})
 *  - field: column name (e.g. "subject", "rationale", "option_a")
 *  - value: current value
 *  - editable: boolean — turns the field into a no-op span when false
 *  - multiline: render as a textarea
 *  - numeric: parse value as number before saving
 *  - placeholder: empty-state placeholder
 *  - className: extra classes for the visible element
 *  - onSaved(updatedQuestion): callback after a successful PATCH
 */
export default function InlineField({
  questionId,
  field,
  value,
  editable = true,
  multiline = false,
  numeric = false,
  placeholder = "—",
  className = "",
  onSaved,
}) {
  const [val, setVal] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setVal(value ?? "");
    setDirty(false);
  }, [value, questionId]);

  const commit = async () => {
    if (!dirty || busy) return;
    let cleaned;
    if (numeric) {
      cleaned = val === "" ? null : Number(val);
    } else {
      cleaned = (val ?? "").toString();
    }
    if (numeric && cleaned !== null && Number.isNaN(cleaned)) {
      toast.error(`${field}: numbers only`);
      setVal(value ?? "");
      setDirty(false);
      return;
    }
    if (cleaned === (value ?? "")) {
      setDirty(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/questions/${questionId}`, { [field]: cleaned });
      setDirty(false);
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setVal(value ?? ""); // revert
      setDirty(false);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur();
    }
    if (e.key === "Escape") {
      setVal(value ?? "");
      setDirty(false);
      ref.current?.blur();
    }
  };

  if (!editable) {
    return (
      <span className={className} data-testid={`inline-readonly-${field}`}>
        {value ?? placeholder}
      </span>
    );
  }

  const baseInputClasses = `w-full px-1.5 py-0.5 rounded-md border border-transparent hover:border-slate-200 hover:bg-slate-50 focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-500/15 outline-none transition ${className}`;

  if (multiline) {
    return (
      <textarea
        ref={ref}
        value={val}
        onChange={(e) => {
          setVal(e.target.value);
          setDirty(true);
        }}
        onBlur={commit}
        onKeyDown={onKeyDown}
        rows={Math.min(6, Math.max(2, (val || "").split("\n").length + 1))}
        placeholder={placeholder}
        data-testid={`inline-field-${field}`}
        className={baseInputClasses}
      />
    );
  }

  return (
    <input
      ref={ref}
      type={numeric ? "number" : "text"}
      step={numeric ? "0.5" : undefined}
      value={val}
      onChange={(e) => {
        setVal(e.target.value);
        setDirty(true);
      }}
      onBlur={commit}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      data-testid={`inline-field-${field}`}
      className={baseInputClasses}
    />
  );
}
